// ---------------------------------------------------------------------------
// KeyboardShortcutsDialog — Modal overlay showing all keyboard shortcuts.
//
// Triggered by pressing "?" or clicking the keyboard icon in the toolbar.
// ---------------------------------------------------------------------------

import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { MOD, SHORTCUT } from '@/lib/shortcuts'
import { cn } from '@/lib/utils'

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform)

// ---------------------------------------------------------------------------
// Shortcut data
// ---------------------------------------------------------------------------

interface ShortcutEntry {
  label: string
  keys: string
}

interface ShortcutGroup {
  title: string
  shortcuts: ShortcutEntry[]
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: 'General',
    shortcuts: [
      { label: 'Undo', keys: SHORTCUT.undo },
      { label: 'Redo', keys: SHORTCUT.redo },
      { label: 'Export JSON', keys: SHORTCUT.save },
      { label: 'Import JSON', keys: SHORTCUT.open },
      { label: 'Search', keys: SHORTCUT.search },
      { label: 'Toggle Preview', keys: SHORTCUT.preview },
    ],
  },
  {
    title: 'Selection',
    shortcuts: [
      { label: 'Select All', keys: SHORTCUT.selectAll },
      { label: 'Deselect / Parent', keys: 'Esc' },
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      { label: 'Previous Sibling', keys: '↑' },
      { label: 'Next Sibling', keys: '↓' },
      { label: 'Enter Child', keys: '↵' },
    ],
  },
  {
    title: 'Editing',
    shortcuts: [
      { label: 'Copy', keys: SHORTCUT.copy },
      { label: 'Cut', keys: SHORTCUT.cut },
      { label: 'Paste', keys: SHORTCUT.paste },
      { label: 'Duplicate', keys: SHORTCUT.duplicate },
      { label: 'Move Up', keys: SHORTCUT.moveUp },
      { label: 'Move Down', keys: SHORTCUT.moveDown },
      { label: 'Delete', keys: SHORTCUT.delete },
    ],
  },
  {
    title: 'Zoom',
    shortcuts: [
      { label: 'Zoom to Fit', keys: SHORTCUT.zoomToFit },
      { label: 'Zoom to 100%', keys: SHORTCUT.zoomTo100 },
      { label: 'Zoom In', keys: SHORTCUT.zoomIn },
      { label: 'Zoom Out', keys: SHORTCUT.zoomOut },
    ],
  },
]

// ---------------------------------------------------------------------------
// Kbd — styled keyboard key
// ---------------------------------------------------------------------------

function Kbd({ children }: { children: string }) {
  // Split compound shortcuts like ⌘⇧Z into individual keys
  const parts = splitKeys(children)
  return (
    <span className="flex items-center gap-0.5">
      {parts.map((part, i) => (
        <kbd
          key={i}
          className={cn(
            'inline-flex h-5 min-w-5 items-center justify-center rounded',
            'border border-border bg-muted px-1',
            'text-[11px] font-medium text-muted-foreground',
          )}
        >
          {part}
        </kbd>
      ))}
    </span>
  )
}

/** Split shortcut display string into individual key tokens */
function splitKeys(s: string): string[] {
  // Handle "Ctrl+Shift+Z" style
  if (s.includes('+')) return s.split('+')
  // Handle Mac symbols: ⌘⇧Z → [⌘, ⇧, Z]
  const macSymbols = ['⌘', '⇧', '⌥', '⌃']
  const parts: string[] = []
  let rest = s
  for (const sym of macSymbols) {
    if (rest.startsWith(sym)) {
      parts.push(sym)
      rest = rest.slice(sym.length)
    }
  }
  if (rest) parts.push(rest)
  return parts.length > 0 ? parts : [s]
}

// ---------------------------------------------------------------------------
// Dialog component
// ---------------------------------------------------------------------------

interface KeyboardShortcutsDialogProps {
  open: boolean
  onClose: () => void
}

export function KeyboardShortcutsDialog({ open, onClose }: KeyboardShortcutsDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [open, onClose])

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 animate-in fade-in-0"
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        className="relative w-[480px] max-h-[80vh] overflow-y-auto rounded-xl border bg-popover shadow-lg animate-in zoom-in-95 fade-in-0"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-popover px-4 py-3">
          <h2 className="text-sm font-semibold">Keyboard Shortcuts</h2>
          <button
            onClick={onClose}
            className="flex h-6 w-6 items-center justify-center rounded-xl text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Shortcut groups */}
        <div className="px-4 py-3 space-y-4">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {group.title}
              </h3>
              <div className="space-y-1">
                {group.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.label}
                    className="flex items-center justify-between rounded-xl px-2 py-1.5 text-sm hover:bg-accent/50 transition-colors"
                  >
                    <span>{shortcut.label}</span>
                    <Kbd>{shortcut.keys}</Kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Footer hint */}
          <div className="border-t pt-3 pb-1">
            <p className="text-center text-[11px] text-muted-foreground">
              Press <Kbd>?</Kbd> to toggle this dialog
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
